import type { Container, Graphics } from "pixi.js";
import { Container as PixiContainer, Graphics as PixiGraphics } from "pixi.js";
import { type FactoryScene, firstArm } from "@/features/simulation/public";
import type { StageLayers } from "@/lib/pixi/createApp";
import { depthOf, MAP_COLS, MAP_ROWS, tileToScreen } from "@/lib/pixi/isoCoords";
import { drawIsoTile, drawOccupationTile, PALETTE } from "@/lib/pixi/textures";

/**
 * シーンエディタ用の Pixi 描画。
 *
 * 目的:
 *  - draft シーンの全オブジェクトを iso タイル上に静的描画
 *  - 選択オブジェクトをハイライト
 *  - placement モード時のホバータイル先行表示 (任意)
 *
 * シミュレータの renderer.ts より簡素 (アニメーションなし、ワーク描画なし)。
 */

export interface EditorRendererHandle {
  render(
    scene: FactoryScene,
    selectionTile: { col: number; row: number } | null,
    routeEditingPartial: { col: number; row: number }[] | null,
  ): void;
  dispose(): void;
}

const COLOR = {
  conveyor: 0x4f4f74,
  conveyorEnd: 0x9c27b0,
  press: 0xff7a3d,
  arm: 0xe63946,
  transport: 0xfdd835,
  transportRoute: 0xa69600,
  sensor: 0x2ec4b6,
  selection: 0x1976d2,
} as const;

export function createEditorRenderer(layers: StageLayers): EditorRendererHandle {
  // 静的: マップ床 (一度だけ描画)
  drawGround(layers.groundLayer);

  // 動的: 毎レンダリングで子要素をクリアして再描画
  const dynamicLayer: Container = new PixiContainer();
  dynamicLayer.sortableChildren = true;
  layers.entityLayer.addChild(dynamicLayer);

  function render(
    scene: FactoryScene,
    selectionTile: { col: number; row: number } | null,
    routeEditingPartial: { col: number; row: number }[] | null,
  ) {
    // 全オブジェクトをクリア
    for (const child of dynamicLayer.removeChildren()) {
      child.destroy({ children: true });
    }

    // コンベア (path のタイル群)
    for (const c of scene.conveyors) {
      for (let i = 0; i < c.path.length; i++) {
        const tile = c.path[i];
        if (!tile) continue;
        const isLast = i === c.path.length - 1;
        const g = new PixiGraphics();
        drawOccupationTile(
          g,
          isLast ? COLOR.conveyorEnd : COLOR.conveyor,
          isLast ? 0.5 : 0.4,
          isLast ? COLOR.conveyorEnd : COLOR.conveyor,
          0.9,
        );
        const { x, y } = tileToScreen(tile.col, tile.row);
        g.position.set(x, y);
        g.zIndex = depthOf(tile.col, tile.row) * 10;
        dynamicLayer.addChild(g);
      }
    }

    // プレス (オレンジ箱)
    for (const p of scene.presses) {
      const g = drawObjectTile(p.position, COLOR.press, 0.55);
      dynamicLayer.addChild(g);
    }

    // アーム本体 (赤)
    for (const a of scene.arms) {
      dynamicLayer.addChild(drawObjectTile(a.position, COLOR.arm, 0.55));
      // pickFrom と placeTo は薄く表示
      dynamicLayer.addChild(drawObjectTile(a.pickFromTile, COLOR.arm, 0.18));
      dynamicLayer.addChild(drawObjectTile(a.placeToTile, COLOR.arm, 0.18));
    }

    // 搬送ロボ: parking + unloading + route (黄色, route は線で繋ぐ)
    for (const t of scene.transports) {
      // route 経路タイル (薄黄)
      for (const tile of t.route) {
        dynamicLayer.addChild(drawObjectTile(tile, COLOR.transportRoute, 0.32));
      }
      // parking (濃黄)
      dynamicLayer.addChild(drawObjectTile(t.parking, COLOR.transport, 0.65));
      // unloading (青系)
      const unloadG = drawObjectTile(t.unloading, 0x4ea5ff, 0.55);
      dynamicLayer.addChild(unloadG);
    }

    // センサー (緑)
    for (const s of scene.sensors) {
      dynamicLayer.addChild(drawObjectTile(s.position, COLOR.sensor, 0.6));
    }

    // 選択ハイライト (青の太い枠)
    if (selectionTile) {
      const g = new PixiGraphics();
      drawOccupationTile(g, COLOR.selection, 0, COLOR.selection, 1);
      const { x, y } = tileToScreen(selectionTile.col, selectionTile.row);
      g.position.set(x, y);
      g.zIndex = 9999;
      dynamicLayer.addChild(g);
    }

    // P4: 経路編集中の partial route をオーバーレイ表示
    if (routeEditingPartial) {
      for (let i = 0; i < routeEditingPartial.length; i++) {
        const tile = routeEditingPartial[i];
        if (!tile) continue;
        const g = new PixiGraphics();
        drawOccupationTile(g, 0x1976d2, 0.45, 0x1976d2, 1);
        const { x, y } = tileToScreen(tile.col, tile.row);
        g.position.set(x, y);
        g.zIndex = 10000 + i;
        dynamicLayer.addChild(g);
      }
    }

    // 参考: arm-1 の symbolic 線などの装飾 (省略)
    void firstArm; // 未使用警告抑制
  }

  function dispose() {
    for (const child of dynamicLayer.removeChildren()) {
      child.destroy({ children: true });
    }
    layers.entityLayer.removeChild(dynamicLayer);
    dynamicLayer.destroy({ children: true });
  }

  return { render, dispose };
}

function drawObjectTile(tile: { col: number; row: number }, color: number, alpha: number): Graphics {
  const g = new PixiGraphics();
  drawOccupationTile(g, color, alpha, color, 0.95);
  const { x, y } = tileToScreen(tile.col, tile.row);
  g.position.set(x, y);
  g.zIndex = depthOf(tile.col, tile.row) * 10 + 5;
  return g;
}

function drawGround(groundLayer: Container) {
  for (let row = 0; row < MAP_ROWS; row++) {
    for (let col = 0; col < MAP_COLS; col++) {
      const g = new PixiGraphics();
      const color = (col + row) % 2 === 0 ? PALETTE.groundLight : PALETTE.groundDark;
      drawIsoTile(g, color, PALETTE.groundAccent);
      const { x, y } = tileToScreen(col, row);
      g.position.set(x, y);
      groundLayer.addChild(g);
    }
  }
}
